import React, { useState, useEffect, useCallback } from 'react';
import { applicants } from '../api';

/**
 * Threaded notes for an applicant.
 *
 * Each note records who wrote it and when. Notes are append-only:
 * there is no edit or delete, so the history stays trustworthy.
 *
 * Shared by ActiveList, HiredList, ReleasedList and TerminatedList.
 */
function ApplicantNotes({ applicantId, canEdit = true }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadNotes = useCallback(async () => {
    if (!applicantId) return;
    setLoading(true);
    setError('');
    try {
      const response = await applicants.getNotes(applicantId);
      setNotes(response.data || []);
    } catch (err) {
      setError('Could not load notes.');
    } finally {
      setLoading(false);
    }
  }, [applicantId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const handleAddNote = async () => {
    const body = newNote.trim();
    if (!body || saving) return;

    setSaving(true);
    setError('');
    try {
      const response = await applicants.addNote(applicantId, body);
      setNotes((prev) => [...prev, response.data]);
      setNewNote('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save note.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return 'Unknown date';
    const date = new Date(value);
    if (isNaN(date.getTime())) return 'Unknown date';
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="review-section">
      <h3 style={{ margin: '0 0 12px 0' }}>Notes</h3>

      {error && (
        <div style={{ color: '#b91c1c', marginBottom: '12px', fontSize: '14px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: '#94a3b8', fontSize: '14px' }}>Loading notes...</div>
      ) : notes.length === 0 ? (
        <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '6px' }}>
          <em style={{ color: '#94a3b8' }}>No notes added yet</em>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
          {notes.map((note) => (
            <div
              key={note.id}
              style={{
                padding: '12px',
                background: '#f8fafc',
                borderRadius: '6px',
                borderLeft: '3px solid #cbd5e1'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: '12px',
                  marginBottom: '6px'
                }}
              >
                <strong style={{ fontSize: '13px', color: '#334155' }}>
                  {note.author_name}
                </strong>
                <span style={{ fontSize: '12px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                  {formatDate(note.created_at)}
                </span>
              </div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>{note.body}</div>
            </div>
          ))}
        </div>
      )}

      {canEdit && (
        <div style={{ marginTop: '12px' }}>
          <textarea
            className="form-textarea"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            rows="3"
            placeholder="Add a note..."
            disabled={saving}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
            <button
              className="btn btn-primary"
              onClick={handleAddNote}
              disabled={saving || !newNote.trim()}
            >
              {saving ? 'Adding...' : 'Add Note'}
            </button>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>
              Notes cannot be edited or deleted once added.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApplicantNotes;
